/**
 * commerce-audit-service.ts
 *
 * Tamper-evident ISO/IEC 27001 audit ledger with SHA-256 hash chaining
 * and ISO/IEC 27701 PII redaction for the Commerce Plugin.
 */

import { generateUUIDv7 } from '../../crypto/uuidv7.js';
import { redactPiiDeep } from '../../identity/pii-redactor.js';
import { sha256Sync } from '../../core/genesis-provisioner.js';
import type {
  CommerceAuditEvent,
  CommerceDataClassification,
  CommerceScope,
} from './commerce-contracts.js';

export interface AuditLogInput {
  readonly scope: CommerceScope;
  readonly actionType: string;
  readonly resourceUri: string;
  readonly outcome: 'allowed' | 'denied' | 'failed';
  readonly reasonCode?: string;
  readonly correlationId: string;
  readonly dataClassification?: CommerceDataClassification;
  readonly details?: Record<string, unknown>;
}

export class CommerceAuditService {
  private readonly ledger: CommerceAuditEvent[] = [];
  private sequenceCounter = 0;
  private latestEventHash: string | undefined = undefined;

  /**
   * Deterministic SHA-256 computation in Node / Browser environment.
   */
  static async computeSha256(data: string): Promise<string> {
    return sha256Sync(data);
  }

  /**
   * Canonical JSON stringifier (sorts keys deterministically).
   */
  static canonicalize(obj: unknown): string {
    if (obj === null || typeof obj !== 'object') {
      return JSON.stringify(obj);
    }
    if (Array.isArray(obj)) {
      return `[${obj.map((item) => CommerceAuditService.canonicalize(item)).join(',')}]`;
    }
    const record = obj as Record<string, unknown>;
    const sortedKeys = Object.keys(record).sort();
    const keyVals = sortedKeys.map(
      (k) => `${JSON.stringify(k)}:${CommerceAuditService.canonicalize(record[k])}`
    );
    return `{${keyVals.join(',')}}`;
  }

  /**
   * Appends an audit record to the ledger with deep PII redaction and SHA-256 hash chaining.
   */
  async recordEvent(input: AuditLogInput): Promise<CommerceAuditEvent> {
    this.sequenceCounter += 1;
    const sequence = this.sequenceCounter;
    const eventId = generateUUIDv7();
    const timestampUtc = new Date().toISOString();

    // ISO/IEC 27701 Deep PII Redaction
    const sanitizedDetails = input.details ? (redactPiiDeep(input.details) as Record<string, unknown>) : {};

    const previousEventHash = this.latestEventHash;

    const payloadToHash = {
      eventId,
      sequence,
      meshId: input.scope.meshId,
      workspaceId: input.scope.workspaceId,
      actorPrincipalId: input.scope.actorPrincipalId,
      actorDeviceKeyId: input.scope.actorDeviceKeyId,
      actionType: input.actionType,
      resourceUri: input.resourceUri,
      outcome: input.outcome,
      reasonCode: input.reasonCode,
      policyVersion: input.scope.policyVersion,
      correlationId: input.correlationId,
      dataClassification: input.dataClassification ?? 'internal',
      detailsRedacted: sanitizedDetails,
      previousEventHash,
      timestampUtc,
    };

    const canonicalString = CommerceAuditService.canonicalize(payloadToHash);
    const eventHash = await CommerceAuditService.computeSha256(canonicalString);

    const event: CommerceAuditEvent = {
      ...payloadToHash,
      eventHash,
    };

    this.ledger.push(event);
    this.latestEventHash = eventHash;

    return event;
  }

  /**
   * Returns copy of the audit ledger.
   */
  getLedger(): readonly CommerceAuditEvent[] {
    return [...this.ledger];
  }

  /**
   * Validates the cryptographic integrity of the audit chain from genesis to tip.
   */
  async verifyChainIntegrity(): Promise<{ valid: boolean; failedIndex?: number; reason?: string }> {
    let expectedPreviousHash: string | undefined = undefined;

    for (let i = 0; i < this.ledger.length; i++) {
      const event = this.ledger[i];

      // 1. Verify previous hash chaining
      if (event.previousEventHash !== expectedPreviousHash) {
        return {
          valid: false,
          failedIndex: i,
          reason: `Broken chain link at index ${i}: expected previous hash ${expectedPreviousHash}, found ${event.previousEventHash}`,
        };
      }

      // 2. Recompute current event hash
      const payloadToHash = {
        eventId: event.eventId,
        sequence: event.sequence,
        meshId: event.meshId,
        workspaceId: event.workspaceId,
        actorPrincipalId: event.actorPrincipalId,
        actorDeviceKeyId: event.actorDeviceKeyId,
        actionType: event.actionType,
        resourceUri: event.resourceUri,
        outcome: event.outcome,
        reasonCode: event.reasonCode,
        policyVersion: event.policyVersion,
        correlationId: event.correlationId,
        dataClassification: event.dataClassification,
        detailsRedacted: event.detailsRedacted,
        previousEventHash: event.previousEventHash,
        timestampUtc: event.timestampUtc,
      };

      const canonicalString = CommerceAuditService.canonicalize(payloadToHash);
      const computedHash = await CommerceAuditService.computeSha256(canonicalString);

      if (computedHash !== event.eventHash) {
        return {
          valid: false,
          failedIndex: i,
          reason: `Content hash mismatch at sequence ${event.sequence}: expected ${event.eventHash}, calculated ${computedHash}`,
        };
      }

      expectedPreviousHash = event.eventHash;
    }

    return { valid: true };
  }
}
